import {Address2ComponentValue, } from './address2-component.domain';
import { CountryOfCitizenshipValue, IndividualFormValue } from './individual-form-component.domain';
import { IdentificationComponentValue } from './identification-component.domain';
import { UploadFileValue } from './file-upload-component.domain';
import { TitleSelectionValue, } from './title-selection-component.domain';
import { YesNoValue} from './yes-no-component.domain';
import {Moment} from 'moment';
import {IndividualWithResult, ThirdPartyEntity} from '../digital-id-service.domain';
import {Observable} from 'rxjs';
import {SignerRoleTypes} from '../application-service.domain';
import {UploadedDocumentRef} from '../types.domain';
import {BusinessNumberSearchResult} from './business-number-search-component.domain';

export interface IndividualFormDialogData {
  existingIndividuals: IndividualWithResult[];
  businessSearchFn: (businessNumber: string) => Observable<BusinessNumberSearchResult>;
  /**
   * Pre-fill the form from a KYB stakeholder being materialised as an individual (KYC/AML tab).
   * Only name and date of birth are known from KYB; the rest is captured in the dialog.
   */
  prefill?: {
    firstName?: string;
    lastName?: string;
    dob?: Moment | null;
  };
  /**
   * Existing details to edit rather than a blank add. The confirmed value comes back on
   * `individualFormValue`; the dialog persists nothing, so the caller decides when to write it.
   */
  editIndividual?: IndividualFormValue;
  /**
   * Whether to offer the ID document upload. Defaults to on for an add and off for an edit, which an
   * edit that is completing a record captured without one (a KYB-materialised individual) overrides.
   */
  showIdDocumentUpload?: boolean;
  /**
   * An ID document is already held against this individual. The file is never read back into the
   * form, so without this the upload's own requirement could not be satisfied on an edit.
   */
  idDocumentProvided?: boolean;
  /** ID documents already filed against this individual, listed under the upload. */
  existingIdDocuments?: UploadedDocumentRef[];
  /** Role to pre-select (e.g. the Guarantor/Signer tick that opened the dialog, or a forced Trustee/Partner). */
  initialRole?: SignerRoleTypes | 'Trustee' | 'Partner';
  /** Locks the role selector to initialRole, hiding the other options (e.g. a forced Trustee add). */
  lockRole?: boolean;
  /**
   * Party roles offered beyond the default signer/guarantor set. When trustee/partner are offered,
   * an owning entity selector is shown for those choices.
   */
  allowTrusteePartnerRoles?: boolean;
  /** Entities that can own a trustee/partner (trusts and partnerships on the application). */
  ownerEntities?: { commercialEntityId: number; entityName: string }[];
  /**
   * Active application people that can be linked to the KYB stakeholder instead of creating a
   * duplicate individual. Present only for KYB reconciliation.
   */
  existingPersonOptions?: {
    applicationIndividualRoleId: number | null;
    individualId: number;
    name: string;
    dateOfBirth: string | null;
    suggested: boolean;
  }[];
}

export interface IndividualFormDialogResult {
  cancelled: boolean;
  valid: boolean;
  applicationIndividualId?: number;
  selectedRole?: SignerRoleTypes;
  /**
   * The raw <individual-form> value, for callers that stage the result on their own form control
   * rather than mapping it onto an add payload.
   */
  individualFormValue?: IndividualFormValue;
  individual?: {
    title: Exclude<TitleSelectionValue, null>,
    firstName: string,
    middleName: string,
    lastName: string,
    residentialAddress: Exclude<Address2ComponentValue, null>,
    dob: Moment,
    email: string,
    role: 'Partner' | 'Director' | 'Guarantor' | 'Shareholder' | 'Trustee' | null,
    signerRole: SignerRoleTypes,
    mobile: string,
    guarantor: Exclude<YesNoValue, null>,
    hasAlias: boolean,
    aliasNames: string | null,
    countryOfCitizenship: CountryOfCitizenshipValue,
    identification?: IdentificationComponentValue,
    // in-memory ID document files (driver licence / passport); the caller uploads these after the individual is created
    idDocument?: UploadFileValue,
    thirdPartyEntity?: ThirdPartyEntity,
    contact : string,
    /** Owning entity for a trustee/partner; undefined for application-level parties. */
    parentCommercialEntityId?: number,
  }
}
